// 管理者: 学習データ S3 アップロード画面(設計書 §3.5.7)
//
// パフォーマー個別の fine-tuning 用データを S3 に直接 PUT する。
// API は presigned URL のみ発行し、ブラウザが直接 S3 へ PUT する設計のため、
// API サーバーは大容量ファイルを通さない(設計書 §3.5.7)。

import { useRef, useState } from 'react';
import { ApiClientError } from '../../api/client';
import { usePerformersList } from '../../api/admin/performers';
import {
  isStubUploadUrl,
  putFileToS3,
  useIssueTrainingDataUploadUrl,
  type UploadUrlResult,
} from '../../api/admin/training-data';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';

const MAX_FILE_BYTES = 1024 ** 3; // 1 GiB(API 側と一致)

interface UploadResult {
  ok: boolean;
  message: string;
  url?: UploadUrlResult;
}

export function AdminTrainingDataUploadPage() {
  // 100 件まで取得(本番は数百〜数千だが Phase 5 の admin 一覧と同じ前提)
  const performersQuery = usePerformersList({ per_page: 100, status: 'active' });
  const issueMutation = useIssueTrainingDataUploadUrl();

  const [performerUlid, setPerformerUlid] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function reset() {
    setFile(null);
    setProgress(null);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setResult(null);
    if (!performerUlid) {
      setResult({ ok: false, message: 'パフォーマーを選択してください' });
      return;
    }
    if (!file) {
      setResult({ ok: false, message: 'ファイルを選択してください' });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setResult({
        ok: false,
        message: `ファイルサイズが上限(1 GiB)を超えています:${formatBytes(file.size)}`,
      });
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const url = await issueMutation.mutateAsync({
        performer_ulid: performerUlid,
        filename: sanitizeFilename(file.name),
        size_bytes: file.size,
        content_type: file.type || 'application/octet-stream',
      });

      if (isStubUploadUrl(url.upload_url)) {
        // dev (StubS3PresignedUrlIssuer) は到達不可ホストを返すので PUT スキップ
        setResult({
          ok: true,
          message:
            'presigned URL を発行しました(dev: stub のため実 PUT はスキップ)。本番 S3 接続後に実際のアップロードが走ります。',
          url,
        });
        reset();
        return;
      }

      await putFileToS3({
        url: url.upload_url,
        headers: url.headers,
        file,
        onProgress: (loaded, total) => {
          setProgress(Math.floor((loaded / total) * 100));
        },
      });
      setResult({
        ok: true,
        message: `アップロード完了: ${url.s3_path}`,
        url,
      });
      reset();
    } catch (err) {
      setResult({
        ok: false,
        message:
          err instanceof ApiClientError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'アップロードに失敗しました',
      });
      setUploading(false);
    } finally {
      setUploading(false);
    }
  }

  return (
    <AdminLayout
      title="学習データアップロード"
      description="パフォーマーごとのファインチューニング用データを S3 に直接アップロードします(最大 1 GiB)。"
    >
      <Card title="アップロード">
        <form onSubmit={handleSubmit}>
          <Select
            label="対象パフォーマー"
            value={performerUlid}
            onChange={(e) => setPerformerUlid(e.target.value)}
          >
            <option value="">— 選択 —</option>
            {(performersQuery.data?.data ?? []).map((p) => (
              <option key={p.ulid} value={p.ulid}>
                {p.display_name}({p.email})
              </option>
            ))}
          </Select>

          <FilePicker
            file={file}
            onChange={setFile}
            inputRef={fileInputRef}
            disabled={uploading}
          />

          {file && (
            <Input
              label="ファイル情報"
              value={`${file.name} — ${formatBytes(file.size)} — ${file.type || 'application/octet-stream'}`}
              disabled
              readOnly
            />
          )}

          {progress !== null && (
            <ProgressBar percent={progress} label={uploading ? 'アップロード中…' : '完了'} />
          )}

          {result && (
            <p
              style={{
                margin: '0 0 12px',
                fontSize: '11px',
                color: result.ok
                  ? 'var(--color-success-text)'
                  : 'var(--color-error-text)',
              }}
            >
              {result.message}
            </p>
          )}

          {result?.ok && result.url && (
            <UploadResultDetail result={result.url} />
          )}

          <div style={{ maxWidth: 240 }}>
            <Button type="submit" disabled={uploading || !performerUlid || !file}>
              {uploading ? 'アップロード中…' : 'アップロード'}
            </Button>
          </div>
        </form>
      </Card>
    </AdminLayout>
  );
}

function FilePicker({
  file,
  onChange,
  inputRef,
  disabled,
}: {
  file: File | null;
  onChange: (f: File | null) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  disabled?: boolean;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label
        style={{
          display: 'block',
          fontSize: '11px',
          color: '#d1d5db',
          marginBottom: 6,
        }}
      >
        ファイル
      </label>
      <input
        ref={inputRef}
        type="file"
        disabled={disabled}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        style={{
          width: '100%',
          padding: '9px 11px',
          background: 'var(--color-input)',
          border: 'var(--border-strong)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-text-primary)',
          fontSize: '12px',
        }}
      />
      <p
        style={{
          margin: '4px 0 0',
          fontSize: '10px',
          color: 'var(--color-text-tertiary)',
        }}
      >
        英数字 / ドット / アンダースコア / ハイフンのみ。最大 1 GiB。{file ? '' : '未選択'}
      </p>
    </div>
  );
}

function ProgressBar({ percent, label }: { percent: number; label: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--color-text-secondary)',
          marginBottom: 4,
        }}
      >
        {label} ({percent}%)
      </div>
      <div
        style={{
          width: '100%',
          height: 6,
          background: 'rgba(255,255,255,0.04)',
          border: 'var(--border-default)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: '100%',
            background: 'var(--gradient-cta)',
            transition: 'width 200ms ease-out',
          }}
        />
      </div>
    </div>
  );
}

function UploadResultDetail({ result }: { result: UploadUrlResult }) {
  return (
    <Card>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: '110px 1fr',
          rowGap: 6,
          margin: 0,
          fontSize: '11px',
        }}
      >
        <dt style={{ color: 'var(--color-text-tertiary)' }}>S3 path</dt>
        <dd
          style={{
            margin: 0,
            color: 'var(--color-text-secondary)',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
          }}
        >
          {result.s3_path}
        </dd>
        <dt style={{ color: 'var(--color-text-tertiary)' }}>有効期限</dt>
        <dd style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
          {new Date(result.expires_at).toLocaleString('ja-JP')}
        </dd>
      </dl>
    </Card>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GiB`;
}

// API 側の filename 制約 ^[A-Za-z0-9._-]+$ に合うように、英数字・. _ - 以外を _ に置換。
function sanitizeFilename(name: string): string {
  return name.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 255);
}
